package net.frozenbit.games.soundgame.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.clipToBounds
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import net.frozenbit.games.soundgame.components.Scoring
import net.frozenbit.games.soundgame.theme.Typography
import net.frozenbit.games.soundgame.viewmodels.HostViewModel
import kotlin.math.min
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.nanoseconds

@Composable
fun HostGameOverScreen(
    scorings: List<HostViewModel.HostUiState.GameOver.Scoring>,
    credits: String,
) {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center,
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp, Alignment.Top),
            modifier = Modifier
                .padding(8.dp)
        ) {
            Row(
                verticalAlignment = Alignment.Bottom,
                horizontalArrangement = Arrangement.spacedBy(16.dp, Alignment.CenterHorizontally),
                modifier = Modifier
                    .padding(top = 80.dp)
            ) {
                for (scoring in scorings) {
                    Scoring(
                        player = scoring.player,
                        points = scoring.points,
                        winner = scoring.winner,
                        modifier = Modifier
                            .offset(y = -(20 * scoring.points).dp)
                    )
                }
            }
        }
        ScrollingCredits(
            credits = credits,
            modifier = Modifier
                .align(Alignment.BottomStart)
                .padding(bottom = 32.dp, start = 32.dp)
        )
    }
}

@Composable
private fun ScrollingCredits(
    credits: String,
    modifier: Modifier = Modifier,
) {
    val textStyle = TextStyle(
        color = Color.White,
        fontSize = Typography.Host.bodySize * 0.4,
        fontFamily = Typography.audiowide(),
        lineHeight = Typography.Host.bodySize * 0.5 * 1.25f,
        textAlign = TextAlign.Center,
    )
    val creditLines = remember(credits) { credits.lines().ifEmpty { listOf("") } }
    val listState = rememberLazyListState()

    LaunchedEffect(listState, creditLines) {
        var previousFrameNanos = withFrameNanos { it }
        while (true) {
            val frameNanos = withFrameNanos { it }
            val elapsedSeconds = min(
                frameNanos - previousFrameNanos,
                16.milliseconds.inWholeNanoseconds
            ) / 1_000_000_000f
            listState.scrollBy(elapsedSeconds * 30f)
            previousFrameNanos = frameNanos
        }
    }

    LazyColumn(
        state = listState,
        userScrollEnabled = false,
        modifier = modifier
            .fillMaxWidth(0.2f)
            .aspectRatio(4f/3f)
            .clip(RoundedCornerShape(32.dp))
            .background(Color.Black)
    ) {
        items(count = (creditLines.size + 1) * 50) { index ->
            val cursorPos = index % creditLines.size
            if (cursorPos == 0) {
                Spacer(modifier = Modifier.height(200.dp))
            } else {
                Text(
                    text = creditLines[cursorPos - 1],
                    style = textStyle,
                    modifier = Modifier.fillMaxWidth(),
                )
            }
        }
    }
}